import { ChevronDown } from 'lucide-react';
import type { ComponentProps } from 'react';

import { Badge, type BadgeProps } from '@vs-shop/ui/badge';
import { clsxm } from '@vs-shop/utils';

import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The product filter rail: one facet's worth of selectable options, repeated
 * down a listing's side column and inside a mobile drawer. Flat named parts a
 * tenant arranges in its own JSX, each painting itself.
 *
 *   <FilterRailFacet>
 *     <FilterRailSearch id={…} value={query} onValueChange={setQuery} … />
 *
 *     <FilterRailScroller>
 *       <FilterRailOptions>
 *         {options.map((option) => (
 *           <FilterRailOption key={option.value}>
 *             <FilterRailOptionLabel htmlFor={option.id}>
 *               <FilterRailOptionControl>
 *                 <Checkbox variant="compact" id={option.id} checked={…} onCheckedChange={…} />
 *                 <FilterRailOptionText>{option.label}</FilterRailOptionText>
 *               </FilterRailOptionControl>
 *               <FilterRailOptionCount>{option.count}</FilterRailOptionCount>
 *             </FilterRailOptionLabel>
 *           </FilterRailOption>
 *         ))}
 *       </FilterRailOptions>
 *
 *       <FilterRailShowAll onClick={showAll}>{showAllLabel}</FilterRailShowAll>
 *     </FilterRailScroller>
 *   </FilterRailFacet>
 *
 * Three things the rail deliberately does NOT decide:
 *
 * - **Whether an option carries a count.** A count badge is a part, so a rail
 *   whose backend reports facet counts composes one and a rail whose backend
 *   does not omits it. That is the composition answering "do I have a count",
 *   not an appearance the section has to be told about.
 * - **What an option's control is.** The checkbox is `@vs-shop/ui`'s and the
 *   tenant wires it to whatever holds selection state, so the rail never sees a
 *   `checked` prop travel through it.
 * - **What any of it says.** Every string — placeholder, clear label, empty
 *   state, "show all" — enters as children or props.
 *
 * No `variant` anywhere: a facet row paints the same in every rail, and what
 * differs between rails is which parts are composed.
 *
 * Server-first. The two parts that must run on the client — the search field
 * and the scroll container — are separate leaves beside this file; nothing
 * here holds state or a directive.
 */

/**
 * What the rail is called, standing over the facets on a wide enough page.
 *
 * The element is the page's — a rail is furniture a listing places, so this
 * renders a neutral box and a page composes `@vs-shop/ui/heading` through
 * `render` at the level its outline calls for. It carries its own hiding: the
 * rail is a drawer below the tablet step, and a title over a drawer that is not
 * there is a title nobody can read.
 */
export type FilterRailTitleProps = ComponentProps<'div'> & {
  /** The element to paint instead of the neutral `<div>`. */
  readonly render?: RenderElement;
};

/** The rail's name. */
export const FilterRailTitle = ({ className, render, ...props }: FilterRailTitleProps) => (
  <RenderedPart
    dataSlot="filter-rail-title"
    className={clsxm(
      'lap:text-[26px]/[28px] tab-lg:block mb-5 hidden text-[18px]/[27px]',
      className
    )}
    render={render}
    {...props}
  />
);

/**
 * One facet's body: its search field, if it has one, above its options.
 *
 * The gap is the facet's own rather than the accordion's, so a facet rendered
 * outside one — the colour swatches, which need no scroller — still separates
 * its parts the same way.
 */
const filterRailFacetClasses = 'flex flex-col gap-y-2';

export type FilterRailFacetProps = ComponentProps<'div'>;

/** One facet's body. */
export const FilterRailFacet = ({ className, ...props }: FilterRailFacetProps) => (
  <div
    data-slot="filter-rail-facet"
    className={clsxm(filterRailFacetClasses, className)}
    {...props}
  />
);

/** The options themselves, stacked. */
const filterRailOptionsClasses = 'flex flex-col gap-y-0.5';

export type FilterRailOptionsProps = ComponentProps<'ul'>;

/** The stacked option list. */
export const FilterRailOptions = ({ className, ...props }: FilterRailOptionsProps) => (
  <ul
    data-slot="filter-rail-options"
    className={clsxm(filterRailOptionsClasses, className)}
    {...props}
  />
);

/**
 * One selectable option's row.
 *
 * The vertical margin is the row's own and not a gap the list hands it: a row
 * composed on its own — the warehouse facet renders two, outside any generated
 * list — has to sit the same way as one inside a list of forty.
 */
const filterRailOptionClasses = 'my-2';

export type FilterRailOptionProps = ComponentProps<'li'>;

/** One option's row. */
export const FilterRailOption = ({ className, ...props }: FilterRailOptionProps) => (
  <li
    data-slot="filter-rail-option"
    className={clsxm(filterRailOptionClasses, className)}
    {...props}
  />
);

/**
 * The row's click surface, spanning it end to end.
 *
 * A `<label>` rather than a click handler on the row: pointing the label at the
 * control's own `id` is what makes the whole row toggle it, keeps the accessible
 * name attached to the control, and costs no JavaScript. `items-start` so a
 * label that wraps to two lines keeps its checkbox level with the first of
 * them rather than centred against both.
 */
const filterRailOptionLabelClasses = 'flex cursor-pointer items-start justify-between gap-x-2';

export type FilterRailOptionLabelProps = ComponentProps<'label'>;

/** The row's click surface. */
export const FilterRailOptionLabel = ({ className, ...props }: FilterRailOptionLabelProps) => (
  <label
    data-slot="filter-rail-option-label"
    className={clsxm(filterRailOptionLabelClasses, className)}
    {...props}
  />
);

/** The control and its text, held together at the leading edge of the row. */
const filterRailOptionControlClasses = 'flex items-start gap-x-3.5';

export type FilterRailOptionControlProps = ComponentProps<'div'>;

/** The control paired with its text. */
export const FilterRailOptionControl = ({ className, ...props }: FilterRailOptionControlProps) => (
  <div
    data-slot="filter-rail-option-control"
    className={clsxm(filterRailOptionControlClasses, className)}
    {...props}
  />
);

/** What the option is called. */
const filterRailOptionTextClasses = 'text-sm font-semibold';

export type FilterRailOptionTextProps = ComponentProps<'span'>;

/** The option's text. */
export const FilterRailOptionText = ({ className, ...props }: FilterRailOptionTextProps) => (
  <span
    data-slot="filter-rail-option-text"
    className={clsxm(filterRailOptionTextClasses, className)}
    {...props}
  />
);

export type FilterRailOptionCountProps = Omit<BadgeProps, 'variant' | 'rounded' | 'size'>;

/**
 * How many products the option would leave. A part rather than a prop on the
 * row, so a rail whose backend reports no counts composes one fewer child
 * instead of passing an absent number down through the row.
 *
 * Binds `@vs-shop/ui`'s `Badge` to the one appearance a facet count has, which
 * is why the three appearance props are not the caller's to set. It renders no
 * element of its own and so carries no `data-slot` of its own either — the
 * badge names its two, and a wrapper added just to hold a third would be an
 * element in the row for no one.
 */
export const FilterRailOptionCount = ({ className, ...props }: FilterRailOptionCountProps) => (
  <Badge
    variant="neutral"
    rounded="full"
    size="none"
    className={clsxm('px-2 font-semibold', className)}
    {...props}
  />
);

/** The colour swatches, wrapping across the rail's width. */
const filterRailColorOptionsClasses = 'm-1 flex flex-wrap items-center gap-3';

export type FilterRailColorOptionsProps = ComponentProps<'ul'>;

/**
 * The colour swatch grid.
 *
 * `m-1` is the room the swatches' focus rings and checked borders draw into —
 * without it the outermost ring is clipped by whatever the rail nests in.
 */
export const FilterRailColorOptions = ({ className, ...props }: FilterRailColorOptionsProps) => (
  <ul
    data-slot="filter-rail-color-options"
    className={clsxm(filterRailColorOptionsClasses, className)}
    {...props}
  />
);

export type FilterRailColorOptionProps = ComponentProps<'li'>;

/**
 * One swatch's cell.
 *
 * No recipe, and therefore no `clsxm`: the grid does the spacing and the cell
 * paints nothing of its own, so there is nothing for a `className` to merge
 * OVER. Passing it straight through also keeps a cell given none from emitting
 * an empty `class` attribute.
 */
export const FilterRailColorOption = (props: FilterRailColorOptionProps) => (
  <li data-slot="filter-rail-color-option" {...props} />
);

/** The swatch's click surface: the swatch, then whatever reads under it. */
const filterRailColorOptionLabelClasses = 'flex cursor-pointer flex-col items-center gap-1';

export type FilterRailColorOptionLabelProps = ComponentProps<'label'>;

/** The swatch's click surface. */
export const FilterRailColorOptionLabel = ({
  className,
  ...props
}: FilterRailColorOptionLabelProps) => (
  <label
    data-slot="filter-rail-color-option-label"
    className={clsxm(filterRailColorOptionLabelClasses, className)}
    {...props}
  />
);

/** The count under a swatch — smaller than a row's badge, which has a row to fill. */
const filterRailColorOptionCountClasses = 'text-[11px]';

export type FilterRailColorOptionCountProps = ComponentProps<'span'>;

/** The count under a swatch. */
export const FilterRailColorOptionCount = ({
  className,
  ...props
}: FilterRailColorOptionCountProps) => (
  <span
    data-slot="filter-rail-color-option-count"
    className={clsxm(filterRailColorOptionCountClasses, className)}
    {...props}
  />
);

/**
 * What the colour is called, for anything that cannot see it.
 *
 * A swatch's entire visible content is the colour, so without this the option
 * has no accessible name at all. Visually hidden rather than absent: the name
 * has to reach the accessibility tree, and a `title` alone does not reliably.
 */
const filterRailColorOptionTextClasses = 'sr-only';

export type FilterRailColorOptionTextProps = ComponentProps<'span'>;

/** The colour's name, visually hidden. */
export const FilterRailColorOptionText = ({
  className,
  ...props
}: FilterRailColorOptionTextProps) => (
  <span
    data-slot="filter-rail-color-option-text"
    className={clsxm(filterRailColorOptionTextClasses, className)}
    {...props}
  />
);

/**
 * The affordance that reveals the options held back by a first-page slice.
 *
 * The chevron belongs to the part rather than to the call site: "there is more
 * below" is the same statement in every tenant, and leaving it composed made
 * each rail restate it. The label is still the caller's, because that is copy.
 */
const filterRailShowAllClasses =
  'text-destructive flex cursor-pointer items-center gap-2 py-2 text-sm';

export type FilterRailShowAllProps = ComponentProps<'button'>;

/** The "show all" affordance. */
export const FilterRailShowAll = ({ className, children, ...props }: FilterRailShowAllProps) => (
  <button
    type="button"
    data-slot="filter-rail-show-all"
    className={clsxm(filterRailShowAllClasses, className)}
    {...props}
  >
    {children}
    <ChevronDown className="text-destructive h-4 w-4" />
  </button>
);
